// src/components/EmployeePortal.js
import React, { useState, useEffect } from 'react';
import logger from '../utils/logger';
import { Routes, Route, Navigate } from 'react-router-dom';
import UserPortalLayout from './UserPortalLayout';
import { useAuth } from '../auth/AuthContext';
import BusinessCentralService from '../services/businessCentralService';
import EmployeeProfile from './portal/EmployeeProfile';
import EmployeeDocuments from './portal/EmployeeDocuments';

// Import PdfPreviewModal component
import PdfPreviewModal from './common/PdfPreviewModal';

const formatDate = (dateString) => {
  if (!dateString) return '';
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;
    return date.toLocaleDateString('en-GB');
  } catch {
    return dateString;
  }
};

// Dashboard content component
const Dashboard = () => {
  const { user, getAccessToken, activeEnvironment } = useAuth();
  const [userData, setUserData] = useState(null);
  const [payslips, setPayslips] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [clientCompanies, setClientCompanies] = useState([]);
  const [isLoadingPdf, setIsLoadingPdf] = useState(false);
  const [pdfModalOpen, setPdfModalOpen] = useState(false);
  const [currentPdf, setCurrentPdf] = useState(null);
  const [currentPdfTitle, setCurrentPdfTitle] = useState('');

  // Initialize Business Central service — only recreate when activeEnvironment changes
  const bcService = React.useMemo(() => {
    return new BusinessCentralService(getAccessToken);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeEnvironment]);
  
  // Function to fetch client companies using the BusinessCentralService
  const fetchClientCompanies = async () => {
    if (!activeEnvironment) {
      logger.error('No active environment selected');
      return;
    }

    // Skip fetch if environment already has a valid company_Id
    const knownCompanyId = activeEnvironment.company_Id;
    if (knownCompanyId && knownCompanyId !== '00000000-0000-0000-0000-000000000000') {
      logger.log('Company ID already known from environment, skipping BC companies fetch:', knownCompanyId);
      localStorage.setItem('clientCompanyId', knownCompanyId);
      if (activeEnvironment.companyName) {
        localStorage.setItem('clientCompanyName', activeEnvironment.companyName);
      }
      return;
    }

    try {
      logger.log('Fetching client companies from Business Central...');
      
      const registrationId = localStorage.getItem('registrationId');
      
      if (!registrationId) {
        logger.error('Missing registrationId for client companies fetch');
        return;
      }
      
      logger.log(`Using registration ID: ${registrationId}`);
      logger.log(`Using environment name: ${activeEnvironment.name}`);
      
      // Use the BusinessCentralService to fetch companies (similar to other BC API calls)
      const companies = await bcService.getClientTenantCompanies(
        activeEnvironment.name,
        registrationId
      );
      
      // Log the companies data to the console
      logger.log('CLIENT COMPANIES FROM BUSINESS CENTRAL:');
      logger.log(companies);
      
      // Log each company ID to the console
      if (Array.isArray(companies)) {
        logger.log('COMPANY IDs:');
        companies.forEach(company => {
          logger.log(`Company ID: ${company.id || company.companyId}, Name: ${company.name || company.companyName || company.displayName}`);
        });
        
        // Store client company information in localStorage for use by other components and backend
        // Store all company details to allow for matching by name later
        localStorage.setItem('clientCompanies', JSON.stringify(companies));
        
        // Find matching company if environment has a companyName
        if (activeEnvironment.companyName) {
          // Use only exact name matching without any fallback
          const matchingCompany = companies.find(company => {
            const envName = activeEnvironment.companyName.trim().toLowerCase();
            const companyName = (company.name || '').trim().toLowerCase();
            const displayName = (company.displayName || '').trim().toLowerCase();
            
            // Log the comparison to help with debugging
            logger.log(`Comparing environment company "${envName}" with BC company "${companyName}" / displayName: "${displayName}"`);
            
            // Case-insensitive exact match - no partial matching or word similarity
            if (companyName === envName || displayName === envName) {
              logger.log('Found exact match!');
              return true;
            }
            
            return false;
          });
          
          if (matchingCompany) {
            logger.log(`Found matching company for "${activeEnvironment.companyName}": ${matchingCompany.id}`);
            localStorage.setItem('clientCompanyId', matchingCompany.id);
            localStorage.setItem('clientCompanyName', matchingCompany.name || matchingCompany.displayName);
          } else {
            logger.warn(`No exact matching company found for "${activeEnvironment.companyName}"`);
            // No fallback - leave clientCompanyId unset if no exact match found
            localStorage.removeItem('clientCompanyId');
            localStorage.removeItem('clientCompanyName');
          }
        } else {
          // No company name specified, ensure we don't use any default
          logger.warn('No company name specified in environment, cannot determine company ID');
          localStorage.removeItem('clientCompanyId');
          localStorage.removeItem('clientCompanyName');
        }
        
        // Store in state
        setClientCompanies(companies);
      } else {
        logger.error('Invalid companies data format:', companies);
      }
    } catch (error) {
      logger.error('Error fetching client companies:', error?.message || error);
    }
  };
  
  // Fetch employee data and client companies on component mount
  useEffect(() => {
    const fetchEmployeeData = async () => {
      if (!user || !activeEnvironment) return;
      
      try {
        setIsLoading(true);
        
        // Fetch client companies first (this is the new requirement)
        await fetchClientCompanies();
        
        // Fetch user profile from Business Central
        const employeeProfile = await bcService.getEmployeeProfile();
        
        // Save employeeNo in localStorage for use by payslips API and notifications
        localStorage.setItem('employeeNo', '');
        if (employeeProfile.employeeNo) {
          logger.log(`Storing employeeNo in localStorage: ${employeeProfile.employeeNo}`);
          localStorage.setItem('employeeNo', employeeProfile.employeeNo);
          // Notify layout components (e.g. notification bell) that employeeNo is now available
          window.dispatchEvent(new CustomEvent('employeeNoReady'));
        } else {
          logger.warn('No employeeNo found in employee profile');
        }
        
        // Update state with employee data
        const fullName = [employeeProfile.firstName, employeeProfile.middleName, employeeProfile.lastName]
            .filter(Boolean) // removes undefined, null, or empty strings
            .join(' ');
        setUserData({
          name: fullName || user.name,
          position: employeeProfile.jobTitle || 'Employee',
          department: activeEnvironment.department || '',
          employeeId: employeeProfile.employeeNo || 'N/A',
          email: employeeProfile.email || user.email,
          joinDate: employeeProfile.employmentDate || 'N/A',
          manager: employeeProfile.manager || 'N/A',
          address: employeeProfile.address || 'N/A',
          address2: employeeProfile.address2 || 'N/A',
          addressLine3: employeeProfile.addressLine3 || '',
          addressLine4: employeeProfile.addressLine4 || '',
          birthDate: employeeProfile.birthDate || 'N/A',
          gender: employeeProfile.gender || 'N/A',
          payFrequency: employeeProfile.frequency || 'N/A',
          taxCode: employeeProfile.hmrcTaxCode || 'N/A',
          niNumber: employeeProfile.nationalInsuranceNumber || 'N/A',
          niCategory: employeeProfile.niClassCode +' '+ employeeProfile.nationalInsuranceCode || 'N/A',
          hoursWorked: employeeProfile.weeklyHoursWorked || 'N/A',
          phone: employeeProfile.mobilePhoneNo || 'N/A',
          city: employeeProfile.city || '',
          postCode: employeeProfile.postCode || '',
          // Include environment info
          environment: activeEnvironment.name,
          environmentType: activeEnvironment.type
        });
        
        // Fetch payslips
        const payslipData = await bcService.getPayslips();
        setPayslips(payslipData || []);

      } catch (error) {
        logger.error('Error fetching employee data:', error?.message || error);
        // If API is not available yet, use mock data
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchEmployeeData();
  }, [user, bcService, activeEnvironment]);
  
  // Function to manually fetch client companies
  const handleManualClientCompanyFetch = async () => {
    try {
      await fetchClientCompanies();
    } catch (error) {
      logger.error('Error in manual client company fetch:', error?.message || error);
    }
  };
  
  // Function to handle payslip PDF view/download
  const handlePayslipPdf = async (payslip, type = 'view') => {
    if (!payslip || !payslip.systemId) {
      logger.error('No payslip systemId provided');
      return;
    }
    
    try {
      setIsLoadingPdf(true);
      const pdfData = await bcService.getPayslipPdf(payslip.systemId);
      
      if (!pdfData) {
        logger.error('No PDF data returned');
        return;
      }
      
      if (type === 'view') {
        // Show in the modal
        setCurrentPdf(pdfData);
        setCurrentPdfTitle(`Payslip - ${payslip.period}`);
        setPdfModalOpen(true);
      } else if (type === 'download') {
        // Convert base64 to Blob for download
        const byteCharacters = atob(pdfData);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: 'application/pdf' });
        
        // Create URL for the blob
        const blobUrl = URL.createObjectURL(blob);
        
        // Create download link
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = `Payslip-${payslip.period.replace(/\s+/g, '-')}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        // Cleanup
        setTimeout(() => URL.revokeObjectURL(blobUrl), 30000);
      }
    } catch (error) {
      logger.error('Error handling payslip PDF:', error?.message || error);
    } finally {
      setIsLoadingPdf(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      {/* PDF Preview Modal */}
      <PdfPreviewModal
        isOpen={pdfModalOpen}
        onClose={() => setPdfModalOpen(false)}
        pdfData={currentPdf}
        title={currentPdfTitle}
      />

      {/* Employee Summary Card */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex items-start">
          <div className="flex-shrink-0">
            <div className="h-16 w-16 rounded-full bg-indigo-100 flex items-center justify-center">
              <span className="text-xl font-bold text-indigo-600">
                {(userData?.name || '').split(' ').map(n => n[0]).join('')}
              </span>
            </div>
          </div>
          <div className="ml-6">
            <h3 className="text-xl font-semibold text-gray-900">{userData?.name}</h3>
            <p className="text-gray-600">{userData?.position} • {userData?.department}</p>
            <div className="mt-2 flex flex-wrap">
              <span className="px-2 py-1 text-xs font-medium rounded-full bg-indigo-100 text-indigo-800 mr-2 mb-2">
                Employee ID: {userData?.employeeId}
              </span>
              <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800 mr-2 mb-2">
                Joined: {formatDate(userData?.joinDate)}
              </span>
              <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800 mb-2">
                {userData?.payFrequency} Pay
              </span>
            </div>
          </div>
        </div>
      </div>
      
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Latest Payslip */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-3">Latest Payslip</h3>
          {payslips && payslips.length > 0 ? (
            <>
              <p className="text-2xl font-bold text-gray-900">£{payslips[0].netPay.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
              <p className="text-sm text-gray-600 mt-1">Period: {payslips[0].period}</p>
              <p className="text-sm text-gray-600">Paid on: {formatDate(payslips[0].payDate)}</p>
            </>
          ) : (
            <p className="text-gray-500">No payslips found</p>
          )}
        </div>
        
        {/* Tax Summary */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-3">Tax Information</h3>
          <p className="text-lg font-semibold text-gray-900">Tax Code: {userData?.taxCode}</p>
          <p className="text-sm text-gray-600 mt-1">NI Number: {userData?.niNumber}</p>
          <p className="text-sm text-gray-600">NI Category: {userData?.niCategory}</p>
        </div>
        
        {/* Contact Information */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-3">Contact Information</h3>
          <div className="space-y-2">
            <div>
              <p className="text-xs text-gray-400">Email</p>
              <p className="text-sm font-medium text-gray-900 truncate">{userData?.email}</p>
            </div>
            <div>
              <p className="text-xs text-gray-400">Phone</p>
              <p className="text-sm font-medium text-gray-900">{userData?.phone}</p>
            </div>
            <div>
              <p className="text-xs text-gray-400">Address</p>
              <p className="text-sm font-medium text-gray-900">
                {userData?.address}
                {userData?.address2 && userData.address2 !== 'N/A' && <span>, {userData.address2}</span>}
                {userData?.addressLine3 && <span><br />{userData.addressLine3}</span>}
                {userData?.addressLine4 && <span><br />{userData.addressLine4}</span>}
                {(userData?.city || userData?.postCode) && (
                  <span><br />{[userData.city, userData.postCode].filter(Boolean).join(', ')}</span>
                )}
                {(userData?.county || userData?.country) && (
                  <span><br />{[userData.county, userData.country].filter(Boolean).join(', ')}</span>
                )}
              </p>
            </div>
          </div>
        </div>
      </div>
      
      {/* Recent Payslips */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Recent Payslips</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Period</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Pay Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Gross Pay</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tax</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Net Pay</th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {/* Create an array of 6 items, fill with payslips first, then empty rows */}
              {Array(6).fill(null).map((_, index) => {
                const payslip = (payslips || [])[index];
                return payslip ? (
                  // If we have a payslip for this row, render it
                  <tr key={payslip.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{payslip.period}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatDate(payslip.payDate)}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">£{(payslip.grossPay || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">£{(payslip.taxPaid || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">£{(payslip.netPay || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-center">
                      <div className="flex items-center justify-center space-x-3">
                        <button 
                          onClick={() => handlePayslipPdf(payslip, 'view')}
                          disabled={isLoadingPdf}
                          className="text-indigo-600 hover:text-indigo-900 transition-colors disabled:opacity-50"
                          title="View payslip"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                            <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                            <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
                          </svg>
                        </button>
                        <button 
                          onClick={() => handlePayslipPdf(payslip, 'download')}
                          disabled={isLoadingPdf}
                          className="text-indigo-600 hover:text-indigo-900 transition-colors disabled:opacity-50"
                          title="Download payslip"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                ) : (
                  // Otherwise render an empty row to maintain consistent table height
                  <tr key={`empty-${index}`}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-200">&nbsp;</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-200">&nbsp;</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-200">&nbsp;</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-200">&nbsp;</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-200">&nbsp;</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-200">&nbsp;</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// Placeholder components for other routes
const Payslips = () => (
  <div className="bg-white shadow rounded-lg p-6">
    <h2 className="text-xl font-semibold mb-4">Your Payslips</h2>
    <p>Your payslip data will appear here.</p>
  </div>
);

const Benefits = () => (
  <div className="bg-white shadow rounded-lg p-6">
    <h2 className="text-xl font-semibold mb-4">Your Benefits</h2>
    <p>Your benefits information will appear here.</p>
  </div>
);

const Support = () => (
  <div className="bg-white shadow rounded-lg p-6">
    <h2 className="text-xl font-semibold mb-4">Support Center</h2>
    <p>Contact support or view FAQs here.</p>
  </div>
);

const Settings = () => (
  <div className="bg-white shadow rounded-lg p-6">
    <h2 className="text-xl font-semibold mb-4">Account Settings</h2>
    <p>Manage your account settings here.</p>
  </div>
);

// Payslips page component
const PayslipsPage = () => {
  const { user, getAccessToken, activeEnvironment } = useAuth();
  const [payslips, setPayslips] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingPdf, setIsLoadingPdf] = useState(false);
  const [pdfModalOpen, setPdfModalOpen] = useState(false);
  const [currentPdf, setCurrentPdf] = useState(null);
  const [currentPdfTitle, setCurrentPdfTitle] = useState('');

  // Initialize Business Central service — only recreate when activeEnvironment changes
  const bcService = React.useMemo(() => {
    return new BusinessCentralService(getAccessToken);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeEnvironment]);
  
  useEffect(() => {
    const fetchPayslips = async () => {
      if (!user || !activeEnvironment) return;
      
      try {
        setIsLoading(true);
        const payslipData = await bcService.getPayslips();
        setPayslips(payslipData || []);
      } catch (error) {
        logger.error('Error fetching payslips:', error?.message || error);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchPayslips();
  }, [user, bcService, activeEnvironment]);
  
  // Function to handle payslip PDF view/download
  const handlePayslipPdf = async (payslip, type = 'view') => {
    if (!payslip || !payslip.systemId) {
      logger.error('No payslip systemId provided');
      return;
    }
    
    try {
      setIsLoadingPdf(true);
      const pdfData = await bcService.getPayslipPdf(payslip.systemId);
      
      if (!pdfData) {
        logger.error('No PDF data returned');
        return;
      }
      
      if (type === 'view') {
        // Show in the modal
        setCurrentPdf(pdfData);
        setCurrentPdfTitle(`Payslip - ${payslip.period}`);
        setPdfModalOpen(true);
      } else if (type === 'download') {
        // Convert base64 to Blob for download
        const byteCharacters = atob(pdfData);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: 'application/pdf' });
        
        // Create URL for the blob
        const blobUrl = URL.createObjectURL(blob);
        
        // Create download link
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = `Payslip-${payslip.period.replace(/\s+/g, '-')}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        // Cleanup
        setTimeout(() => URL.revokeObjectURL(blobUrl), 30000);
      }
    } catch (error) {
      logger.error('Error handling payslip PDF:', error?.message || error);
    } finally {
      setIsLoadingPdf(false);
    }
  };
  
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      {/* PDF Preview Modal */}
      <PdfPreviewModal
        isOpen={pdfModalOpen}
        onClose={() => setPdfModalOpen(false)}
        pdfData={currentPdf}
        title={currentPdfTitle}
      />
      
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">All Payslips</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Period</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Pay Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Gross Pay</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tax</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Net Pay</th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {(payslips || []).map((payslip) => (
                <tr key={payslip.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{payslip.period}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatDate(payslip.payDate)}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">£{(payslip.grossPay || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">£{(payslip.taxPaid || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">£{(payslip.netPay || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-center">
                    <div className="flex items-center justify-center space-x-3">
                      <button 
                        onClick={() => handlePayslipPdf(payslip, 'view')}
                        disabled={isLoadingPdf}
                        className="text-indigo-600 hover:text-indigo-900 transition-colors disabled:opacity-50"
                        title="View payslip"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                          <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                          <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
                        </svg>
                      </button>
                      <button 
                        onClick={() => handlePayslipPdf(payslip, 'download')}
                        disabled={isLoadingPdf}
                        className="text-indigo-600 hover:text-indigo-900 transition-colors disabled:opacity-50"
                        title="Download payslip"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const EmployeePortal = () => {
  return (
    <Routes>
      <Route path="/" element={<UserPortalLayout />}>
        <Route index element={<Navigate to="/portal/dashboard" />} />
        <Route path="dashboard" element={<Dashboard />} />
        <Route path="documents" element={<EmployeeDocuments />} />
        <Route path="profile" element={<EmployeeProfile />} />
        <Route path="*" element={<Navigate to="/portal/dashboard" />} />
      </Route>
    </Routes>
  );
};

export default EmployeePortal;